import React, { useEffect, useState } from 'react';
import { useEncounterStore } from '../../stores/encounterStore';
import { useCheatStore } from '../../stores/cheatStore';
import { useGameStore } from '../../stores/gameStore';
import { useNpcStore } from '../../stores/npcStore';
import { useLootStore } from '../../stores/lootStore';
import { useDiscoveryStore } from '../../stores/discoveryStore';
import { useQuestStore } from '../../stores/questStore';
import { encounterService, openNpcContinuation } from '../../services/encounterService';
import { gameService } from '../../services/gameService';
import { launchCombatScene } from '../../services/combatService';
import { npcService } from '../../services/npcService';
import { EncounterNodeType, SkillCheckOutcome } from '@hexcrawl/shared-utils';
import { PendingDots } from '../common/PendingDots';
import { SkillCheckResult } from './SkillCheckResult';
import { EffectDisplay } from './EffectDisplay';
import { AppearancePreview } from '../character/appearance/AppearancePreview';
import { StaticSpritePreview } from '../character/appearance/StaticSpritePreview';
import { ATTRIBUTE_ICONS as ATTR_EMOJIS, ATTRIBUTE_LABELS as ATTR_LABELS, SKILL_ICONS as SKILL_EMOJIS, SKILL_LABELS } from '../../constants/entityDisplay';
import { CheatGroup } from '../common/CheatGroup';
import { cheatButtonStyle, cheatFailureButtonStyle, cheatSuccessButtonStyle } from '../../constants/cheatStyles';

export const EncounterOverlay: React.FC = () => {
  const activeEncounter = useEncounterStore((s) => s.activeEncounter);
  const currentNode = useEncounterStore((s) => s.currentNode);
  const npcName = useEncounterStore((s) => s.npcName);
  const npcPublicData = useEncounterStore((s) => s.npcPublicData);
  const skillCheckResult = useEncounterStore((s) => s.skillCheckResult);
  const appliedEffects = useEncounterStore((s) => s.appliedEffects);
  const updateEncounter = useEncounterStore((s) => s.updateEncounter);
  const updateCurrentNode = useEncounterStore((s) => s.updateCurrentNode);
  const setNpcPublicData = useEncounterStore((s) => s.setNpcPublicData);
  const clearActiveEncounter = useEncounterStore((s) => s.clearActiveEncounter);
  const setSkillCheckResult = useEncounterStore((s) => s.setSkillCheckResult);
  const clearSkillCheckResult = useEncounterStore((s) => s.clearSkillCheckResult);
  const setAppliedEffects = useEncounterStore((s) => s.setAppliedEffects);
  const clearAppliedEffects = useEncounterStore((s) => s.clearAppliedEffects);
  const character = useGameStore((s) => s.gameState?.character);
  const cheatsEnabled = useCheatStore((s) => s.cheatsEnabled);
  const addOrUpdateQuest = useQuestStore((s) => s.addOrUpdateQuest);
  const showQuestNotification = useQuestStore((s) => s.showNotification);
  const questNotification = useQuestStore((s) => s.notification);
  const clearQuestNotification = useQuestStore((s) => s.clearNotification);
  const queueBadgePulse = useQuestStore((s) => s.queueBadgePulse);

  const [pendingExit, setPendingExit] = useState<string | null>(null);
  const [pendingComplete, setPendingComplete] = useState(false);
  const [showCheatJson, setShowCheatJson] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

  const handleCopy = (label: string, json: string) => {
    navigator.clipboard.writeText(json);
    setCopiedLabel(label);
    setTimeout(() => setCopiedLabel(null), 1000);
  };

  const npcId = activeEncounter?.npcId;
  useEffect(() => {
    if (!npcId) return;
    console.debug('[EncounterOverlay] Fetching NPC public data for npcId:', npcId);
    npcService.getNpcById(npcId)
      .then((npc) => {
        console.debug('[EncounterOverlay] NPC public data loaded:', { id: npc.id, name: npc.name, spriteType: npc.spriteType, sprite: npc.sprite });
        setNpcPublicData(npc);
      })
      .catch((err) => console.warn('[EncounterOverlay] Failed to load NPC sprite data:', err));
  }, [npcId]);

  if (!activeEncounter || !currentNode) return null;

  // Combat node — server already started combat and caller transitioned to CombatScene
  if (currentNode.type === EncounterNodeType.COMBAT) return null;

  const handleExit = async (targetNodeId: string, cheatForceSkillCheck?: SkillCheckOutcome) => {
    if (pendingExit !== null) return;
    setPendingExit(targetNodeId);
    clearAppliedEffects();
    try {
      const response = await encounterService.updateNode(activeEncounter.id, targetNodeId, cheatForceSkillCheck);
      updateEncounter(response.encounter);
      updateCurrentNode(response.node);

      if (response.skillCheckResult) {
        setSkillCheckResult(response.skillCheckResult);
      }

      if (response.appliedEffects?.length) {
        setAppliedEffects(response.appliedEffects);
        // Re-fetch game state so gold/HP/MP displays reflect the changes
        const gameId = useGameStore.getState().gameState?.id;
        if (gameId) {
          gameService.getState(gameId)
            .then(state => useGameStore.getState().setGameState(state))
            .catch(err => console.error('Failed to refresh game state after effects:', err));
        }
      }

      if (response.pendingLoot) {
        // Defer loot until the encounter closes so it appears after the skill check
        // result and following encounter node, not alongside them.
        useEncounterStore.getState().setPendingLootOnClose(response.pendingLoot);
      }

      const discoveredPlaceIds = response.discoveredPlaceIds ?? [];
      const discoveredItemIds = response.discoveredItemIds ?? [];
      const discoveredNpcIds = response.discoveredNpcIds ?? [];
      if (discoveredPlaceIds.length > 0 || discoveredItemIds.length > 0 || discoveredNpcIds.length > 0) {
        const gameId = useGameStore.getState().gameState?.id;
        if (gameId) {
          useDiscoveryStore.getState().fetchDiscoveries(gameId);
        }
      }

      if (response.hiredNpc) {
        useNpcStore.getState().setNpc(response.hiredNpc);
        const gameId = useGameStore.getState().gameState?.id;
        if (gameId) {
          useDiscoveryStore.getState().fetchDiscoveries(gameId);
        }
      }

      if (response.quest) {
        // A quest we already know about coming back as 'active' is an in-place update, not
        // an acceptance — a failed beat that escalated repoints the objective mid-scene.
        // Refresh the journal, but don't announce it as newly accepted.
        const alreadyTracked = useQuestStore.getState().quests.some(q => q.id === response.quest!.id);
        addOrUpdateQuest(response.quest);
        queueBadgePulse();
        if (response.quest.status === 'completed') {
          showQuestNotification(`Quest completed: ${response.quest.title}`);
        } else if (response.quest.status === 'failed') {
          // A failure escalation just closed the quest out mid-scene. Announce it while the
          // failure text is still on screen, so the journal move isn't silent.
          showQuestNotification(`Quest failed: ${response.quest.title}`);
        } else if (response.quest.status === 'ready_to_complete') {
          showQuestNotification(`Objective complete: ${response.quest.title}`);
        } else if (!alreadyTracked) {
          showQuestNotification(`Quest accepted: ${response.quest.title}`);
        }
        setTimeout(() => clearQuestNotification(), 4000);
      }

      // If the server started combat for the new node, transition to CombatScene
      if (response.combat) {
        const gameState = useGameStore.getState().gameState;
        if (gameState) {
          const updatedGameState = { ...gameState, activeCombat: response.combat };
          useGameStore.getState().setGameState(updatedGameState);
          try { window.phaserGame?.scene?.stop('MapScene'); } catch {}
          await launchCombatScene(updatedGameState);
        }
      }
    } catch (err: any) {
      console.error('Failed to update encounter node:', err);
    } finally {
      setPendingExit(null);
    }
  };

  const handleComplete = async () => {
    if (pendingComplete) return;
    setPendingComplete(true);
    try {
      const response = await encounterService.completeEncounter(activeEncounter.id);

      if (response.appliedEffects?.length) {
        const gameId = useGameStore.getState().gameState?.id;
        if (gameId) {
          gameService.getState(gameId)
            .then(state => useGameStore.getState().setGameState(state))
            .catch(err => console.error('Failed to refresh game state after effects:', err));
        }
      }

      // Prefer deferred node/move loot over completion loot; fall back to completion loot.
      const lootOnClose = useEncounterStore.getState().pendingLootOnClose ?? response.pendingLoot ?? null;
      clearActiveEncounter();
      if (lootOnClose) {
        useLootStore.getState().setPendingLoot(lootOnClose);
      }

      // A quest was just turned in at its giver: re-open a fresh conversation so they
      // can offer new work. Wait for any reward loot to be dismissed first (handled in
      // LootOverlay); with no loot, open it now.
      if (response.followUpNpcId) {
        if (lootOnClose) {
          useEncounterStore.getState().setPendingFollowUpNpcId(response.followUpNpcId);
        } else {
          void openNpcContinuation(response.followUpNpcId);
        }
      }

      // Flush any queued badge pulse now that the encounter is closed.
      // If loot is still showing (set during this encounter or deferred to close), defer to loot dismiss.
      if (!useLootStore.getState().pendingLoot) {
        useQuestStore.getState().flushBadgePulse();
      }
    } catch (err: any) {
      console.error('Failed to complete encounter:', err);
    } finally {
      setPendingComplete(false);
    }
  };

  const exits = currentNode.exits;
  const isTerminal = exits.length === 0;

  const renderNpcSprite = () => {
    if (!npcPublicData) return null;
    console.debug('[EncounterOverlay] Rendering NPC sprite, spriteType:', npcPublicData.spriteType, 'sprite:', npcPublicData.sprite);
    if (npcPublicData.spriteType === 'composite' && npcPublicData.characterDefinition) {
      return <AppearancePreview definition={npcPublicData.characterDefinition} size={96} />;
    }
    if (npcPublicData.spriteType === 'static' && npcPublicData.sprite) {
      return <StaticSpritePreview filename={npcPublicData.sprite} size={96} />;
    }
    console.warn('[EncounterOverlay] NPC has spriteType', npcPublicData.spriteType, 'but missing required sprite data. sprite:', npcPublicData.sprite, 'characterDefinition:', npcPublicData.characterDefinition);
    return null;
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      zIndex: 5000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'monospace',
    }}>
      {questNotification && (
        <div style={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a3a1a',
          border: '1px solid #44aa44',
          borderRadius: 8,
          padding: '8px 18px',
          color: '#88ff88',
          fontSize: 13,
          fontFamily: 'monospace',
          zIndex: 6000,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          {questNotification}
        </div>
      )}
      {cheatsEnabled && showCheatJson && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 7000,
            display: 'flex',
            flexDirection: 'column',
            padding: 24,
            boxSizing: 'border-box',
            overflow: 'hidden',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, display: 'flex', gap: 16, overflow: 'hidden' }}>
            {[
              { label: 'activeEncounter', data: activeEncounter },
              { label: 'currentNode', data: currentNode },
            ].map(({ label, data }) => {
              const json = JSON.stringify(data, null, 2);
              return (
                <div
                  key={label}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexShrink: 0 }}>
                    <span style={{ color: '#ffcc66', fontFamily: 'monospace', fontSize: 12 }}>{label}</span>
                    <button
                      onClick={() => handleCopy(label, json)}
                      style={{
                        background: copiedLabel === label ? '#1a3a1a' : 'transparent',
                        border: `1px solid ${copiedLabel === label ? '#44aa44' : '#2a4a6a'}`,
                        borderRadius: 4,
                        color: copiedLabel === label ? '#88ff88' : '#4a7a9a',
                        fontFamily: 'monospace',
                        fontSize: 10,
                        padding: '2px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      {copiedLabel === label ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre style={{
                    flex: 1,
                    overflow: 'auto',
                    background: '#0a1520',
                    border: '1px solid #1a3a5a',
                    borderRadius: 8,
                    padding: 16,
                    color: '#aaddff',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    margin: 0,
                  }}>
                    {json}
                  </pre>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
            <button
              onClick={() => setShowCheatJson(false)}
              style={{
                background: '#2a1a1a',
                border: '1px solid #aa4444',
                borderRadius: 4,
                color: '#ff8888',
                fontFamily: 'monospace',
                fontSize: 12,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div style={{
        background: '#0e2238',
        border: '2px solid #cc6633',
        borderRadius: 12,
        padding: '24px 36px',
        textAlign: 'center',
        minWidth: 280,
        maxWidth: 520,
        position: 'relative',
      }}>
        {cheatsEnabled && (
          <button
            onClick={() => setShowCheatJson(true)}
            title="Cheat: view encounter JSON"
            style={{
              ...cheatButtonStyle,
              position: 'absolute',
              top: 8,
              right: 8,
              fontSize: 10,
              padding: '2px 6px',
              lineHeight: 1.4,
            }}
          >
            {'Cheat: { }'}
          </button>
        )}
        {skillCheckResult ? (
          <SkillCheckResult
            result={skillCheckResult}
            onDismiss={clearSkillCheckResult}
          />
        ) : (
          <>
            <h2 style={{
              color: '#ffcc66',
              fontSize: 20,
              margin: '0 0 16px 0',
            }}>
              {npcName ?? 'Encounter'}
            </h2>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
              {(() => {
                const sprite = renderNpcSprite();
                return sprite ? <div style={{ flexShrink: 0 }}>{sprite}</div> : null;
              })()}

              {currentNode.text && (
                <div style={{
                  flex: 1,
                  background: '#0a1a2a',
                  border: '1px solid #1a3a5a',
                  borderRadius: 8,
                  padding: '12px 16px',
                  color: '#d0d0d0',
                  fontSize: 14,
                  lineHeight: 1.5,
                  textAlign: 'left',
                }}>
                  {currentNode.text}
                </div>
              )}
            </div>

            {appliedEffects && <EffectDisplay effects={appliedEffects} />}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch', position: 'relative' }}>
              {(pendingExit !== null || pendingComplete) && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}>
                  <PendingDots/>
                </div>
              )}
              <div style={{ visibility: (pendingExit !== null || pendingComplete) ? 'hidden' : 'visible', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
              {isTerminal ? (
                <button
                  onClick={handleComplete}
                  style={{
                    background: '#1a3a5a',
                    color: '#a8d1ff',
                    border: '1px solid #5aa0ff',
                    borderRadius: 6,
                    padding: '8px 32px',
                    fontFamily: 'monospace',
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  OK
                </button>
              ) : (
                exits.map((exit, idx) => {
                  const sc = exit.skillCheck;

                  if (!sc) {
                    return (
                      <button
                        key={`${exit.node}-${idx}`}
                        onClick={() => handleExit(exit.node)}
                        style={{
                          background: '#3a2a1a',
                          color: '#ffcc66',
                          border: '1px solid #cc6633',
                          borderRadius: 6,
                          padding: '8px 24px',
                          fontFamily: 'monospace',
                          fontSize: 14,
                          cursor: 'pointer',
                        }}
                      >
                        {exit.text}
                      </button>
                    );
                  }

                  // Skill check exit — compute modifier from character stats
                  const attrVal = character?.attributes[sc.attribute] ?? null;
                  const skillRank = character?.skills[sc.skill] ?? 0;
                  const mod = attrVal !== null
                    ? Math.floor(attrVal / 2) - 5 + skillRank
                    : null;
                  const modStr = mod !== null ? (mod >= 0 ? `+${mod}` : String(mod)) : '?';
                  const attrValStr = attrVal !== null ? String(attrVal) : '?';

                  const attrEmoji = ATTR_EMOJIS[sc.attribute] ?? sc.attribute;
                  const skillEmoji = SKILL_EMOJIS[sc.skill] ?? sc.skill;
                  const attrLabel = ATTR_LABELS[sc.attribute] ?? sc.attribute;
                  const skillLabel = SKILL_LABELS[sc.skill] ?? sc.skill;

                  const tooltip = `${attrLabel} (${attrValStr}) + ${skillLabel} (${skillRank}) → ${modStr} vs DC ${sc.dc}`;

                  return (
                    <div key={`${exit.node}-${idx}`} style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                      <button
                        onClick={() => handleExit(exit.node)}
                        title={tooltip}
                        style={{
                          flex: 1,
                          background: '#1a2a3a',
                          border: '1px solid #5aa0ff',
                          borderRadius: 6,
                          padding: '8px 16px',
                          fontFamily: 'monospace',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        <span style={{ fontSize: 11, color: '#6699cc', letterSpacing: '0.5px' }}>
                          {attrEmoji} {attrValStr}  {skillEmoji} {skillRank}  ·  {modStr} vs DC {sc.dc}
                        </span>
                        <span style={{ color: '#aaddff', fontSize: 14 }}>
                          {exit.text}
                        </span>
                      </button>
                      {cheatsEnabled && (
                        <CheatGroup vertical>
                          <button
                            onClick={() => handleExit(exit.node, 'success')}
                            title="Cheat: force this skill check to succeed"
                            style={{ ...cheatSuccessButtonStyle, flex: 1, padding: '0 10px' }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleExit(exit.node, 'failure')}
                            title="Cheat: force this skill check to fail"
                            style={{ ...cheatFailureButtonStyle, flex: 1, padding: '0 10px' }}
                          >
                            ✗
                          </button>
                        </CheatGroup>
                      )}
                    </div>
                  );
                })
              )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
